import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useSyncExternalStore,
} from 'react';
import { defaultStorageAdapter, type StorageAdapter } from '../lib/storage-adapter';

// Private to this module — consumers reach it through `useStorage`/`StorageProvider`, never
// directly. Default is the real localStorage-with-fallback adapter, so production needs no
// provider; tests/stories override by wrapping with a `<StorageProvider>` carrying an in-memory
// adapter.
const StorageContext = createContext<StorageAdapter>(defaultStorageAdapter);

export function StorageProvider({
  adapter,
  children,
}: {
  adapter: StorageAdapter;
  children: ReactNode;
}) {
  return <StorageContext.Provider value={adapter}>{children}</StorageContext.Provider>;
}

// One registry for every useStorage instance. Writes notify all of them; each re-reads its own
// key and React bails out via Object.is when its value is unchanged, so unrelated keys don't
// re-render. This is what gives same-document reactivity — a native `storage` event only fires
// cross-tab.
const listeners = new Set<() => void>();
function notify() {
  for (const listener of listeners) listener();
}

/**
 * Reactive, JSON-encoded value persisted via the storage context adapter, synced across all hook
 * instances in the document. Returns `[value, set, clear]`; `value` is `null` when unset. Use a
 * stable `key`. Works without a provider (the context defaults to real localStorage).
 */
export function useStorage<T>(key: string) {
  const storage = useContext(StorageContext);

  const subscribe = useCallback((cb: () => void) => {
    listeners.add(cb);
    return () => listeners.delete(cb);
  }, []);
  const getSnapshot = useCallback(() => storage.getItem(key), [key, storage]);

  const raw = useSyncExternalStore(subscribe, getSnapshot, () => null);

  const value = useMemo<T | null>(() => {
    if (raw === null) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null; // corrupt entry — treat as unset
    }
  }, [raw]);

  const set = useCallback(
    (next: T) => {
      const encoded = JSON.stringify(next);
      if (storage.getItem(key) === encoded) return; // idempotent — avoids redundant re-renders
      storage.setItem(key, encoded);
      notify();
    },
    [key, storage],
  );

  const clear = useCallback(() => {
    if (storage.getItem(key) === null) return;
    storage.removeItem(key);
    notify();
  }, [key, storage]);

  return [value, set, clear] as const;
}
